import {
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import * as React from 'react';
import { ExternalOrderService } from 'services';
import { SnackbarContext } from 'components';
import RefreshIcon from '@material-ui/icons/Refresh';

/**
 * Wolt venue online/offline ribbon for the Live Orders view — mirrors the
 * Bolt provider bar. Reads the venue's live status from Wolt
 * (response.status.is_online) and toggles it via woltSetVenueOnline. Kept
 * minimal on purpose (just the online/offline switch); the old Wolt Ops
 * certification tab was retired 2026-07-28.
 */
const useStyles = makeStyles((theme) => ({
  bar: {
    padding: theme.spacing(1, 2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    borderLeft: '4px solid #1976d2', // Wolt blue, to tell it apart from Bolt
  },
  statusBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  chipOnline: {
    background: '#1b5e20',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    height: 28,
  },
  chipOffline: {
    background: '#b71c1c',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    height: 28,
  },
  chipUnknown: {
    background: '#757575',
    color: 'white',
    fontWeight: 700,
    fontSize: '0.95rem',
    height: 28,
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
}));

interface Props {
  shopId: number;
  /** In the combined status row: no Paper of its own. */
  compact?: boolean;
}

export const WoltProviderStatusBar: React.FC<Props> = ({ shopId, compact }) => {
  const classes = useStyles();
  const snackbar = React.useContext(SnackbarContext);
  const [online, setOnline] = React.useState<boolean | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [pending, setPending] = React.useState<'ONLINE' | 'OFFLINE' | null>(
    null,
  );

  const refresh = React.useCallback(() => {
    setLoading(true);
    ExternalOrderService.woltGetVenueStatus(shopId)
      .then((r: any) => {
        const s = r?.response?.status ?? r?.status;
        setOnline(typeof s?.isOnline === 'boolean' ? s.isOnline : null);
      })
      .catch(() => setOnline(null)) // no token / not onboarded -> UNKNOWN, silent
      .finally(() => setLoading(false));
  }, [shopId]);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const setStatus = async (target: 'ONLINE' | 'OFFLINE') => {
    setPending(target);
    try {
      await ExternalOrderService.woltSetVenueOnline(shopId, { status: target });
      snackbar.open(
        target === 'ONLINE'
          ? 'Wolt: now online, accepting orders'
          : 'Wolt: now offline, no new orders',
      );
      refresh();
    } catch (e) {
      snackbar.error(e);
    } finally {
      setPending(null);
    }
  };

  const statusChip =
    online === true ? (
      <Chip label="ONLINE" className={classes.chipOnline} />
    ) : online === false ? (
      <Chip label="OFFLINE" className={classes.chipOffline} />
    ) : (
      <Chip label="UNKNOWN" className={classes.chipUnknown} />
    );

  return (
    <Paper
      className={classes.bar}
      elevation={compact ? 0 : 1}
      style={compact ? { margin: 0, flex: 1, minWidth: 260 } : undefined}
    >
      <div className={classes.statusBlock}>
        <Typography variant="subtitle2" style={{ marginRight: 4 }}>
          Wolt venue:
        </Typography>
        {statusChip}
        <Tooltip title="Refresh Wolt status">
          <IconButton size="small" onClick={refresh} disabled={loading}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <span className={classes.meta}>{loading ? 'loading…' : ''}</span>
      </div>

      <div className={classes.actions}>
        <Button
          variant="contained"
          size="small"
          style={{ background: '#1976d2', color: '#fff' }}
          disabled={pending !== null || online === true}
          onClick={() => setStatus('ONLINE')}
          startIcon={
            pending === 'ONLINE' ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Go Online
        </Button>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          disabled={pending !== null || online === false}
          onClick={() => setStatus('OFFLINE')}
          startIcon={
            pending === 'OFFLINE' ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Go Offline
        </Button>
      </div>
    </Paper>
  );
};
